import React, {
    ChangeEvent,
    useRef,
    KeyboardEvent,
    useEffect,
    useState,
    useLayoutEffect,
    forwardRef,
    useImperativeHandle,
} from "react";
import attchefileicon from "../../../assets/images/ChatFile.svg";
import { EmojiItem, emojiData } from "../emojiPicker/EmojiPicker";
import "./GlobalChatInput.css";
import {
    ALLOWED_FILE_MIMETYPES,
    MAX_FILE_SIZE_KB,
    MAX_FILE_SIZE_MB,
} from "../../../Data/SD";
import { toast } from "react-toastify";
import UserInfo from "../../../interfaces/UserInfo";

interface GlobalChatInputProps {
    commentText: string;
    onTextChange: (text: string) => void;
    onSendMessage: () => void;
    onSelectFile: (file?: File) => void;
    users?: UserInfo[]; // Add users prop for @ mentions
}

export interface GlobalChatInputRef {
    insertEmoji: (emoji: string) => void;
}

const GlobalChatInput = forwardRef<GlobalChatInputRef, GlobalChatInputProps>(

    ({ commentText, onTextChange, onSendMessage, onSelectFile, users = [] }, ref) => {
        const commentRef = useRef<HTMLDivElement>(null);
        const [isEmojiTagging, setIsEmojiTagging] = useState<boolean>(false);
        const [suggestedEmojis, setSuggestedEmojis] = useState<EmojiItem[]>([]);
        const [highlightedEmojiIndex, setHighlightedEmojiIndex] =
            useState<number>(0);
        const selectionRange = useRef<Range | null>(null);

        // @ mention state
        const [isTagging, setIsTagging] = useState<boolean>(false);
        const [tagSearch, setTagSearch] = useState<string>("");
        const [suggestedUsers, setSuggestedUsers] = useState<UserInfo[]>([]);
        const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
        const [savedRange, setSavedRange] = useState<Range | null>(null);

        useImperativeHandle(ref, () => ({
            insertEmoji: (emoji: string) => {
                if (!commentRef.current) return;

                commentRef.current.focus();
                const sel = window.getSelection();

                if (sel) {
                    let range;
                    if (sel.rangeCount > 0) {
                        range = sel.getRangeAt(0);
                    } else if (selectionRange.current) {
                        range = selectionRange.current;
                        sel.addRange(range);
                    } else {
                        // As a fallback, create a new range at the end
                        range = document.createRange();
                        range.selectNodeContents(commentRef.current);
                        range.collapse(false);
                        sel.addRange(range);
                    }

                    range.deleteContents();
                    const emojiNode = document.createTextNode(emoji);
                    range.insertNode(emojiNode);

                    // Move cursor after the inserted emoji
                    range.setStartAfter(emojiNode);
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);

                    // Update parent state
                    onTextChange(commentRef.current.innerText);
                }
            },
        }));

        // Utility functions for @ mentions
        const getMessageContent = (element: HTMLElement): string => {
            let message = "";
            element.childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    message += node.textContent;
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    const el = node as HTMLElement;
                    if (el.tagName.toLowerCase() === "span" && el.getAttribute("data-tag")) {
                        message += el.getAttribute("data-tag");
                    } else if (el.tagName.toLowerCase() === "br") {
                        message += "\n";
                    } else {
                        message += getMessageContent(el);
                    }
                }
            });
            return message;
        };

        const saveSelection = () => {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                if (commentRef.current?.contains(range.commonAncestorContainer)) {
                    setSavedRange(range.cloneRange());
                }
            }
        };

        const restoreSelection = () => {
            if (savedRange) {
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(savedRange);
            }
        };

        const insertAtCursor = (html: string) => {
            if (!commentRef.current) return;
            commentRef.current.focus();
            restoreSelection();

            const sel = window.getSelection();
            if (sel && sel.getRangeAt(0)) {
                let range = sel.getRangeAt(0);
                range.deleteContents();
                const el = document.createElement("div");
                el.innerHTML = html;
                const frag = document.createDocumentFragment();
                let node, lastNode;
                while ((node = el.firstChild)) {
                    lastNode = frag.appendChild(node);
                }
                range.insertNode(frag);

                if (lastNode) {
                    range = range.cloneRange();
                    range.setStartAfter(lastNode);
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
                onTextChange(getMessageContent(commentRef.current));
                saveSelection();
            }
        };

        useEffect(() => {
            if ((isTagging || isEmojiTagging) && savedRange) {
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(savedRange);
            }
        }, [isTagging, isEmojiTagging, savedRange]);

        // Sync the contentEditable div with the parent's state
        useLayoutEffect(() => {
            if (commentRef.current && commentText !== commentRef.current.innerText) {
                const currentCursorPosition = getCursorPosition(commentRef.current);
                commentRef.current.innerText = commentText;
                setCursorPosition(commentRef.current, currentCursorPosition);
            }
        }, [commentText]);

        const getCursorPosition = (element: HTMLElement) => {
            let pos = 0;
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const preCaretRange = range.cloneRange();
                preCaretRange.selectNodeContents(element);
                preCaretRange.setEnd(range.endContainer, range.endOffset);
                pos = preCaretRange.toString().length;
            }
            return pos;
        };

        const setCursorPosition = (element: HTMLElement, position: number) => {
            const selection = window.getSelection();
            const range = document.createRange();
            let charCount = 0;
            let found = false;

            function traverse(node: Node) {
                if (found) return;
                if (node.nodeType === Node.TEXT_NODE) {
                    const textLength = node.textContent?.length || 0;
                    if (charCount + textLength >= position) {
                        range.setStart(node, position - charCount);
                        range.collapse(true);
                        found = true;
                    } else {
                        charCount += textLength;
                    }
                } else {
                    for (let i = 0; i < node.childNodes.length; i++) {
                        traverse(node.childNodes[i]);
                        if (found) break;
                    }
                }
            }

            traverse(element);

            if (selection && found) {
                selection.removeAllRanges();
                selection.addRange(range);
            } else if (selection) {
                // Fallback to end if position not found
                range.selectNodeContents(element);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        };


        const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const file = e.target.files[0];

                const fileSizeKB = file.size / 1024; // Convert bytes to KB
                if (fileSizeKB > MAX_FILE_SIZE_KB) {
                    toast.warn(
                        `Upload failed: File is larger than the ${MAX_FILE_SIZE_MB} MB limit`
                    );
                    e.target.value = ""; // Reset input
                    return;
                }
                onSelectFile(file);
                e.target.value = "";
            }
        };

        const handleInput = () => {
            if (!commentRef.current) return;
            saveSelection();
            const sel = window.getSelection();
            const range = sel?.getRangeAt(0);
            if (!range) return;

            const textNode = range.startContainer;
            const textContent = textNode.textContent || "";
            const caretOffset = range.startOffset;
            const textBeforeCaret = textContent.slice(0, caretOffset);

            const lastAt = textBeforeCaret.lastIndexOf("@");
            const lastColon = textBeforeCaret.lastIndexOf(":");
            const lastSpace = Math.max(
                textBeforeCaret.lastIndexOf(" "),
                textBeforeCaret.lastIndexOf("\n")
            );

            // Handle @ mentions
            if (lastAt > lastSpace) {
                const search = textBeforeCaret.slice(lastAt + 1).toLowerCase();
                setIsTagging(true);
                setIsEmojiTagging(false);
                setTagSearch(search);
                const filtered = users.filter(
                    (u) =>
                        u.displayName.toLowerCase().startsWith(search) ||
                        u.firstName.toLowerCase().startsWith(search) ||
                        u.lastName.toLowerCase().startsWith(search)
                );
                setSuggestedUsers(filtered);
                setHighlightedIndex(0);
            } else {
                setIsTagging(false);
            }

            // Handle emoji suggestions
            if (lastColon > lastSpace) {
                const searchTerm = textBeforeCaret
                    .substring(lastColon + 1)
                    .toLowerCase();
                if (
                    !searchTerm.trim() ||
                    searchTerm.includes(" ") ||
                    searchTerm.includes(":")
                ) {
                    setIsEmojiTagging(false);
                    return;
                }
                const filtered = emojiData.filter((e) =>
                    e.keywords.some((k) => k.toLowerCase().includes(searchTerm))
                );
                setSuggestedEmojis(filtered);
                setHighlightedEmojiIndex(0);
                setIsEmojiTagging(filtered.length > 0);
            } else {
                setIsEmojiTagging(false);
            }

            onTextChange(getMessageContent(commentRef.current));
        };

        const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
            const suggestionsActive = isTagging && suggestedUsers.length > 0;
            const emojiSuggestionsActive = isEmojiTagging && suggestedEmojis.length > 0;

            if (suggestionsActive) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev + 1) % suggestedUsers.length);
                } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedIndex(
                        (prev) => (prev - 1 + suggestedUsers.length) % suggestedUsers.length
                    );
                } else if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    handleTagSelection(suggestedUsers[highlightedIndex]);
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    setIsTagging(false);
                }
            } else if (emojiSuggestionsActive) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedEmojiIndex(
                        (prev) => (prev + 1) % suggestedEmojis.length
                    );
                } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedEmojiIndex(
                        (prev) =>
                            (prev - 1 + suggestedEmojis.length) % suggestedEmojis.length
                    );
                } else if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    handleEmojiTagSelection(suggestedEmojis[highlightedEmojiIndex].emoji);
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    setIsEmojiTagging(false);
                }
            } else {
                if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSendMessage();
                }
            }
        };

        const handleTagSelection = (user: UserInfo) => {
            if (!commentRef.current) return;
            const tagHtml = `<span contenteditable="false" class="chat-input__mention-badge" data-user-id="${user.id}" data-user-name="@${user.displayName}" spellcheck="false">${user.displayName}</span>&nbsp;`;

            restoreSelection();
            const sel = window.getSelection();
            const range = sel?.getRangeAt(0);

            if (range && range.startContainer.nodeType === Node.TEXT_NODE) {
                const textNode = range.startContainer as Text;
                const textContent = textNode.textContent || "";
                const caretOffset = range.startOffset;
                const lastAt = textContent.slice(0, caretOffset).lastIndexOf("@");

                if (lastAt !== -1) {
                    range.setStart(textNode, lastAt);
                    range.deleteContents();

                    // Insert the tag HTML directly at the deletion position
                    const el = document.createElement("div");
                    el.innerHTML = tagHtml;
                    const frag = document.createDocumentFragment();
                    let node, lastNode;
                    while ((node = el.firstChild)) {
                        lastNode = frag.appendChild(node);
                    }
                    range.insertNode(frag);

                    // Move cursor after the inserted tag
                    if (lastNode) {
                        range.setStartAfter(lastNode);
                        range.collapse(true);
                        sel?.removeAllRanges();
                        sel?.addRange(range);
                    }

                    onTextChange(getMessageContent(commentRef.current));
                    saveSelection();
                }
            }
            setIsTagging(false);
        };

        const handleEmojiTagSelection = (emoji: string) => {
            if (!commentRef.current) return;
            const sel = window.getSelection();
            if (!sel?.rangeCount) return;
            const range = sel.getRangeAt(0);
            if (range.startContainer.nodeType === Node.TEXT_NODE) {
                const textNode = range.startContainer as Text;
                const preCaretText =
                    textNode.textContent?.substring(0, range.startOffset) ?? "";
                const lastColon = preCaretText.lastIndexOf(":");
                if (lastColon !== -1) {
                    const replaceRange = document.createRange();
                    replaceRange.setStart(textNode, lastColon);
                    replaceRange.setEnd(textNode, range.startOffset);
                    sel.removeAllRanges();
                    sel.addRange(replaceRange);

                    // Direct DOM manipulation and then notify parent
                    replaceRange.deleteContents();
                    const emojiNode = document.createTextNode(emoji);
                    replaceRange.insertNode(emojiNode);
                    range.setStartAfter(emojiNode);
                    range.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(range);

                    onTextChange(commentRef.current.innerText);
                }
            }
            setIsEmojiTagging(false);
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
            const items = e.clipboardData.items;
            let handled = false;

            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type.startsWith("image/")) {
                    const file = item.getAsFile();
                    if (file) {
                        onSelectFile(file);
                        handled = true;
                    }
                }
            }

            if (handled) {
                e.preventDefault();
                return;
            }

            // Fallback for text paste
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
        };

        const handleBlur = () => {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                selectionRange.current = sel.getRangeAt(0);
            }
            saveSelection();
        };

        return (
            <div className="w-full flex items-center relative">
                <div className="attachment-btn-container absolute attache-base">
                    <input
                        id="chatFileInput"
                        type="file"
                        style={{ display: "none" }}
                        accept={ALLOWED_FILE_MIMETYPES.join(",")}
                        onChange={handleFileChange}
                    />
                    <label
                        htmlFor="chatFileInput"
                        className="cursor-pointer w-5 h-5"
                        title="Attach a file"
                    >
                        <img src={attchefileicon} alt="Attach file" className="w-5 h-5" />
                    </label>
                </div>
                <div className="chat-input-grid-container">
                    <div
                        className={`chat-input-grid-item chat-input-placeholder ${commentText ? "chat-input-placeholder-hidden" : ""
                            }`}
                    >
                        Type a message...
                    </div>
                    <div
                        className="chat-input-grid-item chat-input-editable"
                        contentEditable
                        onInput={handleInput}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onBlur={handleBlur}
                        ref={commentRef}
                        role="textbox"
                        aria-multiline="true"
                    ></div>
                    {isEmojiTagging && suggestedEmojis.length > 0 && (
                        <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-card-dark border dark:border-card-dark rounded-lg shadow-lg w-full max-h-48 overflow-y-auto z-10">
                            {suggestedEmojis.map((item, index) => (
                                <div
                                    key={`${item.emoji}-${item.keywords[0]}-${index}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className={`cursor-pointer p-2 flex gap-2 items-center ${index === highlightedEmojiIndex
                                        ? "bg-gray-300 dark:bg-gray-700"
                                        : "hover:bg-gray-200 dark:hover:bg-card-dark"
                                        }`}
                                    onClick={() => handleEmojiTagSelection(item.emoji)}
                                >
                                    <span className="text-xl">{item.emoji}</span>
                                    <span>:{item.keywords[0]}:</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {isTagging && suggestedUsers.length > 0 && (
                        <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-card-dark border dark:border-card-dark rounded-lg shadow-lg w-full max-h-48 overflow-y-auto z-10">
                            {suggestedUsers.map((user, index) => (
                                <div
                                    key={user.id}
                                    onMouseDown={(e) => e.preventDefault()}
                                    className={`cursor-pointer p-2 flex gap-2 items-center ${index === highlightedIndex
                                        ? "bg-gray-300 dark:bg-gray-700"
                                        : "hover:bg-gray-200 dark:hover:bg-card-dark"
                                        }`}
                                    onClick={() => handleTagSelection(user)}
                                >
                                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
                                        {user.firstName[0]}{user.lastName[0]}
                                    </div>
                                    <span className="text-sm">{user.displayName}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

export default GlobalChatInput;
